import { event } from "@foretagsplatsen/yaem";
import { remove } from "@foretagsplatsen/core-helpers/array";
import PeriodReport from "./PeriodReport.js";
import periodReportSectionFactory from "./section/periodReportSectionFactory.js";

/**
 * Draft of a period report, not yet published. As it's not published, it
 * can be modified freely.
 *
 * @param {keyFigure[]} [spec.keyFigures] - keyFigures in the period report
 */
export default class PeriodReportDraft extends PeriodReport {
	constructor(spec = {}) {
		super(spec);
		this._keyFigures = spec.keyFigures || [];

		this.draftChanged = event();
		this.commentChanged = event();

		this._sections.forEach((section) => {
			section.changed.register(() => {
				this.draftChanged.trigger(this);
			});

			section.commentChanged.register(() => {
				this.commentChanged.trigger(section);
			});
		});
	}

	setPeriod(period) {
		this._period = period;
		this.draftChanged.trigger(this);
	}

	setAuthor(author) {
		this._author = author;
		this.draftChanged.trigger(this);
	}

	setTitle(title) {
		this._title = title;
		this.draftChanged.trigger(this);
	}

	setSubtitle(subtitle) {
		this._subtitle = subtitle;
		this.draftChanged.trigger(this);
	}

	setComment(comment) {
		this._comment = comment;
		this.commentChanged.trigger(this);
	}

	setSections(sections) {
		this._setSections(sections);
		this.draftChanged.trigger(this);
	}

	getKeyFigures() {
		return this._keyFigures;
	}

	setKeyFigures(keyFigures) {
		this._keyFigures = keyFigures;
		this.draftChanged.trigger(this);
	}

	getAttachments() {
		return this._generatedAttachments.concat(this._uploadedAttachments);
	}

	addAttachments(attachments) {
		attachments.forEach((attachment) => this.addAttachment(attachment));
	}

	addAttachment(attachment) {
		if (attachment.isUploadedAttachment()) {
			this._uploadedAttachments = [
				...this._uploadedAttachments,
				attachment,
			];
		} else {
			this._generatedAttachments = [
				...this._generatedAttachments,
				attachment,
			];
		}
		this.draftChanged.trigger(this);
	}

	removeAttachment(attachment) {
		if (attachment.isUploadedAttachment()) {
			this._uploadedAttachments = remove(
				this._uploadedAttachments,
				attachment,
			);
		} else {
			this._generatedAttachments = remove(
				this._generatedAttachments,
				attachment,
			);
		}
		this.draftChanged.trigger(this);
	}

	updateDefaultAttachments(attachments) {
		this._uploadedAttachments = [
			...attachments,
			...this._uploadedAttachments.filter(
				(attachment) => !attachment.isDefaultAttachment(),
			),
		];

		this.draftChanged.trigger(this);
	}

	withdraw() {
		throw new Error("Cannot withdraw a period report draft");
	}

	isDraft() {
		return true;
	}

	isPublished() {
		return false;
	}

	isWithdrawn() {
		return false;
	}

	getGeneratedAttachments() {
		return this._generatedAttachments;
	}

	getUploadedAttachments() {
		return this._uploadedAttachments;
	}

	serialize() {
		// The draft's key figures to override/supplement the cached system key figures
		const keyFiguresById = this._buildKeyFiguresMap();

		return {
			// author: this._author,
			id: this._id,
			periodString: this._period.toString(),
			subtitle: this._subtitle.toString(),
			title: this._title.toString(),
			comment: this._comment,
			attachments: this.getAttachments().map((attachment) =>
				attachment.serialize(),
			),
			sections: this.getSections().map((section) => section.serialize()),
			keyFigureIds: this._keyFigures.map((keyFigure) =>
				keyFigure.getId(),
			),
			customKeyFigures: this._keyFigures
				.filter((keyFigure) => keyFigure.hasBeenModified())
				.map((keyFigure) =>
					this._serializeKeyFigure({ keyFigure, keyFiguresById }),
				),
		};
	}

	_buildKeyFiguresMap() {
		return Object.fromEntries(
			this._keyFigures.map((keyFigure) => [keyFigure.getId(), keyFigure]),
		);
	}

	_serializeKeyFigure({ keyFigure, keyFiguresById }) {
		const serialized = keyFigure.serialize();

		return {
			...serialized,
			ownerId: this._company.getLegacyId(),
			ownerType: "Company",
			name: serialized.name,
			subtitle: serialized.subtitle,
			description: serialized.description,
			basId: keyFigure.getBasId() || "",
			comment: keyFigure.getComment() || "",
			isKnown: keyFigure.isKnown(),
			knownName: keyFigure.getKnownName() || "",
			numberOfDecimals: keyFigure.getNumberOfDecimals(),
			valueType: serialized.valueType,
			expression: keyFigure
				.getFormula({
					additionalKeyFigures: keyFiguresById,
				})
				.serialize(),
			chartOptions: serialized.chartOptions,
		};
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.keyFigures = data.keyFigureResult.getKeyFigures();
	}

	static newFromExisting({ author, period, company, title, periodReport }) {
		const instance = new this({
			author,
			period,
			company,
			title,
		});

		instance.setKeyFigures(periodReport.getKeyFigures());
		instance.setSubtitle(periodReport.getSubtitle());
		instance.setComment(periodReport.getComment());

		instance.setSections(
			periodReport.getSections().map((section) => {
				const newSection =
					periodReportSectionFactory.newFromExisting(section);
				section.setPeriodReport(instance);
				return newSection;
			}),
		);

		return instance;
	}
}
